import {isEmpty, map, pipe, reject, split, trim, uniq} from 'ramda';

import {RemoteConfigValues, useRemoteConfig} from 'components/app/RemoteConfigProvider';
import {getEnabledNotificationPreferences} from 'config/staticConfig';
import {getCurrentDateEastern, isBeforeDate, isWithinRangeInclusive} from 'utils/date-time';

const parseCSV = (string: string = '') => pipe(split(/\s*,\s*/), map(trim), reject(isEmpty), uniq)(String(string));

const isWithinOutageWindow = (outageStart?: string, outageEnd?: string, passedInDate?: Date) => {
  if (outageStart && outageEnd) {
    return isWithinRangeInclusive(passedInDate || getCurrentDateEastern(), outageStart, outageEnd);
  }
  return false;
};

const isBeforeOutageWindow = (outageStart: string, outageEnd: string) =>
  !!outageStart && !!outageEnd && isBeforeDate(getCurrentDateEastern(), new Date(outageStart));

const toBoolean = (value: string) => value === 'true';

// eslint-disable-next-line
const toJSON = <T>(value: string): T | undefined => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const getFeatures = (config: RemoteConfigValues) => ({
  HasError: config.HasError,
  IsLoading: config.IsLoading,
  AddVehicle: toBoolean(config.AddVehicle),
  AnnuityInterestRates: toBoolean(config.AnnuityInterestRates),
  AutoGlassVendorFNOL: toBoolean(config.AutoGlassVendorFNOL),
  BillingOutageMessaging: isBeforeOutageWindow(config.BillingOutageWindowStart, config.BillingOutageWindowEnd),
  BillingOutageWindow: isWithinOutageWindow(config.BillingOutageWindowStart, config.BillingOutageWindowEnd),
  BillingOutageWindowStart: config.BillingOutageWindowStart,
  BillingOutageWindowEnd: config.BillingOutageWindowEnd,
  BillingLifeOutageWindow: isWithinOutageWindow(config.BillingLifeOutageWindowStart, config.BillingLifeOutageWindowEnd),
  BillingLifeOutageWindowStart: config.BillingLifeOutageWindowStart,
  BillingLifeOutageWindowEnd: config.BillingLifeOutageWindowEnd,
  CEAPolicyInfo: toBoolean(config.CEAPolicyInfo),
  EditAutoCoverages: toBoolean(config.EditAutoCoverages),
  EditDriversLicense: toBoolean(config.EditDriversLicense),
  EditHomeCoverages: toBoolean(config.EditHomeCoverages),
  EditLifeAddress: toBoolean(config.EditLifeAddress),
  EstimateDriverStates: parseCSV(config.EstimateDriverStates),
  EstimateVehicle: toBoolean(config.EstimateVehicle),
  FNOLTextNotification: toBoolean(config.FNOLTextNotification),
  LifeABSBanner: isBeforeDate(getCurrentDateEastern(), 'January 1, 2024'),
  LifeQuoteOnlineApplicationOutage: isWithinOutageWindow(
    config.LifeQuotingApplicationOutageStart,
    config.LifeQuotingApplicationOutageEnd
  ),
  RemoveLender: toBoolean(config.RemoveLender),
  RemoveVehicle: toBoolean(config.RemoveVehicle),
  RemoveVehicleReasonLoggingAPI: toBoolean(config.RemoveVehicleReasonLoggingAPI),
  AppleWallet: toBoolean(config.AppleWallet),
  EnableQuotingCrossSellBanner: toBoolean(config.EnableQuotingCrossSellBanner),
  EnableQuotingBindOnline: toBoolean(config.EnableQuotingBindOnline),
  EnableQuotingBindOnlineDwelling: parseCSV(config.EnableQuotingBindOnlineDwelling),
  EnableEASQuotingBindOnline: toBoolean(config.EnableEASQuotingBindOnline),
  EnableEASQuotingBindOnlineDwelling: parseCSV(config.EnableEASQuotingBindOnlineDwelling),
  EnableSmarterHomeDiscountPromo: parseCSV(config.EnableSmarterHomeDiscountPromo),
  RemoveLenderMaximumLendersAllowed: parseInt(config.RemoveLenderMaximumLendersAllowed, 10),
  TempRemoveWelcomeBannerMobileApp: toBoolean(config.TempRemoveWelcomeBannerMobileApp),
  DisableExpressID: toBoolean(config.DisableExpressID),
  EnabledTextingNotificationPreferences: getEnabledNotificationPreferences(
    config.EnabledTextingNotificationPreferences
  ),
  TempEnableAdobeTargetVECIntegration: toBoolean(config.TempEnableAdobeTargetVECIntegration),
  TempBillingChangePaymentPlan: toBoolean(config.TempBillingChangePaymentPlan),
  TempEnableChangePaymentPlanWithSemiAnnualDiscount: toBoolean(
    config.TempEnableChangePaymentPlanWithSemiAnnualDiscount
  ),
  EnableHeadingDebugging: toBoolean(config.EnableHeadingDebugging),
  AutopayDiscountStates: parseCSV(config.AutopayDiscountStates),
  CalendlyRolloutStates: parseCSV(config.CalendlyRolloutStates),
  CalendlyBannerStates: parseCSV(config.CalendlyBannerStates),
  TempEnableLifeQuoting: toBoolean(config.TempEnableLifeQuoting),
  EnableBoardOfDirectorsLink: toBoolean(config.EnableBoardOfDirectorsLink),
  TempEnableOnboarding: toBoolean(config.TempEnableOnboarding)
});

export const useFeatures = () => getFeatures(useRemoteConfig());
